/**
 * AI Bot 좌측 대화 목록 — AI Center(V2) 엔진일 때만 (2026-10-07, aibot-v2-conversation-rooms).
 *
 * 방을 누르면 대화만 바뀐다. ERP 메뉴는 열지 않는다 — 어느 메뉴에서 시작했는지는 글로만 보여 준다.
 * 숨기기는 언제든 되돌릴 수 있다. "삭제" 라고 쓰지 않는다 — 지우는 것이 아니다.
 * 버튼은 전송 중에도 눌린다. 그래야 늦게 오는 답을 다른 방에 붙이지 않는 방어가 시험된다.
 *
 * 카톡 · 네이트온 PC 처럼 (2026-10-21) — 줄은 제목 + 오른쪽 시간 / 메뉴 이름. 제목 바꾸기 · 숨기기 · 되살리기는
 * 줄 우클릭 메뉴로 — 줄 위 아이콘이 제목 자리를 먹어 선택한 줄만 짧게 잘렸다. 오른쪽 경계선을 끌어 폭을 바꾼다.
 */
import React, { useRef, useState } from "react";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import MapsUgcOutlinedIcon from "@mui/icons-material/MapsUgcOutlined";
import ChatTip from "./ChatTip";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import type { ChatRoom, ChatTag, ChatTagHit } from "@shared/api/chatRoomApi";
import { formatRoomTime, roomTitle } from "@shared/api/chatRoomApi";
import { TagIcon } from "./chatTags";

export type RoomView = "active" | "hidden";

interface Props {
  view: RoomView;
  rooms: ChatRoom[];
  selectedId: string | null;
  query: string;
  loading: boolean;
  error: string | null;
  /** 메뉴코드 → 이름. 없으면 코드 그대로 */
  menuName: (cdMenu: string | null) => string;
  /** 메뉴코드 → 업무 두 글자(구매 · 영업 …). 줄 앞 아이콘. 모르면 null — 챗봇 아이콘 */
  menuGroup?: (cdMenu: string | null) => string | null;
  onViewChange: (view: RoomView) => void;
  onQueryChange: (q: string) => void;
  onRefresh: () => void;
  onSelect: (room: ChatRoom) => void;
  onNew: () => void;
  onRename: (room: ChatRoom, title: string) => void;
  onHide: (room: ChatRoom) => void;
  onRestore: (room: ChatRoom) => void;
  /** 좁은 화면에서 대화창 위에 겹쳐 떴을 때 닫는 버튼. 넓은 화면(옆에 고정)에서는 없다 */
  onClose?: () => void;
  /** 목록 폭(px). 오른쪽 경계선을 끌면 onResize(폭, 끝났는지) */
  width?: number;
  onResize?: (width: number, done: boolean) => void;
  /** 태그로 모아 보기 (2026-10-22) — 🏷 를 누르면 태그 판(onTagOpen), 고른 태그면 목록 대신 그 태그가 달린 말풍선 */
  tagsEnabled?: boolean;
  tagFilter?: ChatTag | null;
  tagHits?: ChatTagHit[] | null;
  tagHitsLoading?: boolean;
  onTagOpen?: (anchor: HTMLElement) => void;
  onTagClear?: () => void;
  onOpenHit?: (hit: ChatTagHit) => void;
}

/** 목록 폭 — 이보다 좁으면 제목이 거의 안 보인다. 넓게는 챗봇 창의 절반까지 */
export const ROOMS_MIN_WIDTH = 200;

/** 업무 아이콘 색 — 글자마다 늘 같은 색(글자 코드 합으로 고른다) */
const AVATAR_COLORS = [
  { bg: "#e0e7ff", fg: "#3730a3" },
  { bg: "#dcfce7", fg: "#166534" },
  { bg: "#fef3c7", fg: "#92400e" },
  { bg: "#fce7f3", fg: "#9d174d" },
  { bg: "#e0f2fe", fg: "#075985" },
  { bg: "#ede9fe", fg: "#5b21b6" },
  { bg: "#ffedd5", fg: "#9a3412" },
  { bg: "#ccfbf1", fg: "#115e59" },
];

/** 대화 줄 앞 아이콘 (2026-10-16) — 시작 메뉴의 업무 두 글자. 메뉴 없이 시작한 대화는 챗봇 */
function RoomAvatar({ label }: { label: string | null }) {
  if (!label) {
    return (
      <span className="chatbot-room-avatar is-bot" aria-hidden="true">
        <SmartToyOutlinedIcon fontSize="inherit" />
      </span>
    );
  }
  const sum = Array.from(label).reduce((n, ch) => n + ch.charCodeAt(0), 0);
  const color = AVATAR_COLORS[sum % AVATAR_COLORS.length];
  return (
    <span className="chatbot-room-avatar" aria-hidden="true" style={{ background: color.bg, color: color.fg }}>
      {label}
    </span>
  );
}

export default function RoomSidebar(props: Props) {
  const {
    view, rooms, selectedId, query, loading, error, menuName, menuGroup,
    onViewChange, onQueryChange, onRefresh, onSelect, onNew, onRename, onHide, onRestore, onClose,
    width, onResize, tagsEnabled, tagFilter, tagHits, tagHitsLoading, onTagOpen, onTagClear, onOpenHit,
  } = props;
  /** 줄 우클릭 메뉴 — 누른 자리에 뜬다 */
  const [roomMenu, setRoomMenu] = useState<{ x: number; y: number; room: ChatRoom } | null>(null);
  const asideRef = useRef<HTMLElement>(null);

  const openRoomMenu = (event: React.MouseEvent, room: ChatRoom) => {
    event.preventDefault();
    /* 키보드 메뉴 키로 열면 좌표가 0 — 줄 아래에 띄운다 */
    let { clientX: x, clientY: y } = event;
    if (x === 0 && y === 0) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      x = rect.left + 16;
      y = rect.bottom;
    }
    setRoomMenu({ x, y, room });
  };

  /* 오른쪽 경계선 끌기 — 챗봇 서랍 폭 조절과 같은 방식 */
  const startResize = (event: React.MouseEvent) => {
    const aside = asideRef.current;
    if (!aside || !onResize) return;
    event.preventDefault();
    const left = aside.getBoundingClientRect().left;
    const max = Math.max(ROOMS_MIN_WIDTH, (aside.parentElement?.clientWidth ?? 0) / 2);
    let latest = aside.offsetWidth;
    const move = (e: MouseEvent) => {
      latest = Math.round(Math.min(Math.max(e.clientX - left, ROOMS_MIN_WIDTH), max));
      onResize(latest, false);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      onResize(latest, true);
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  /** Enter 로 제출한 직후 입력칸이 사라지며 blur 가 한 번 더 온다 — 두 번 보내지 않는다 */
  const renameDoneRef = useRef(false);

  const startRename = (room: ChatRoom) => {
    renameDoneRef.current = false;
    setEditing({ id: room.idConv, title: room.title || "" });
  };

  const submitRename = (room: ChatRoom) => {
    if (renameDoneRef.current || !editing || editing.id !== room.idConv) return;
    renameDoneRef.current = true;
    const title = editing.title.trim();
    setEditing(null);
    if (title && title !== (room.title || "")) onRename(room, title);
  };

  return (
    <aside
      aria-label="대화 목록"
      className="chatbot-sidebar chatbot-rooms"
      ref={asideRef}
      style={width ? { flexBasis: width, width } : undefined}
    >
      {onResize && (
        <div
          aria-label="대화 목록 폭 조절"
          className="chatbot-rooms-resizer"
          onMouseDown={startResize}
          role="separator"
        />
      )}
      <div className="chatbot-rooms-head">
        {/* 머리의 [새 대화]와 같은 아이콘 (2026-10-16) */}
        <button className="chatbot-side-item" onClick={onNew} type="button">
          <MapsUgcOutlinedIcon fontSize="small" />
          <span>새 대화</span>
        </button>
        {onClose && (
          <ChatTip title="닫기">
            <button aria-label="대화 목록 닫기" className="chatbot-rooms-close" onClick={onClose} type="button">
              <CloseIcon fontSize="inherit" />
            </button>
          </ChatTip>
        )}
      </div>

      <div className="chatbot-side-search">
        <div className="chatbot-rooms-search">
          <SearchIcon fontSize="inherit" />
          <input
            aria-label="대화 제목 검색"
            maxLength={100}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="제목 검색"
            value={query}
          />
        </div>
        {/* 태그로 모든 대화방에서 모아 보기 (2026-10-22) */}
        {tagsEnabled && onTagOpen && (
          <ChatTip title="태그">
            <button
              aria-label="태그로 찾기"
              className={`chatbot-rooms-tagbtn${tagFilter ? " is-on" : ""}`}
              onClick={(e) => onTagOpen(e.currentTarget)}
              type="button"
            >
              <LocalOfferOutlinedIcon fontSize="inherit" />
            </button>
          </ChatTip>
        )}
      </div>

      {/* 고른 태그 — 목록 대신 그 태그가 달린 말풍선. 누르면 그 방을 열고 그 말풍선으로 */}
      {tagFilter && (
        <div className="chatbot-taghits">
          <div className="chatbot-taghits-head">
            <TagIcon code={tagFilter.cdIcon} size={16} />
            <span className="chatbot-taghits-name">{tagFilter.nmTag}</span>
            <ChatTip title="닫기">
              <button aria-label="태그 모아 보기 닫기" className="chatbot-rooms-close" onClick={onTagClear} type="button">
                <CloseIcon fontSize="inherit" />
              </button>
            </ChatTip>
          </div>
          <div className="chatbot-side-list" role="list">
            {tagHitsLoading && <p className="chatbot-side-empty">불러오는 중…</p>}
            {!tagHitsLoading && (tagHits ?? []).length === 0 && (
              <p className="chatbot-side-empty">이 태그를 단 말풍선이 없습니다.</p>
            )}
            {!tagHitsLoading &&
              (tagHits ?? []).map((hit) => (
                <button
                  className="chatbot-taghit"
                  key={`${hit.idConv}-${hit.noSeq}-${hit.answer ? "a" : "q"}`}
                  onClick={() => onOpenHit?.(hit)}
                  role="listitem"
                  type="button"
                >
                  <span className="chatbot-side-chat-top">
                    <span className="chatbot-side-chat-name">{hit.title || "제목 없음"}</span>
                    <span className="chatbot-side-chat-time">{formatRoomTime(hit.tmTurn)}</span>
                  </span>
                  <span className="chatbot-taghit-text">
                    <span className="chatbot-taghit-who">{hit.answer ? "답" : "질문"}</span>
                    {hit.snippet || "\u00a0"}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}

      {!tagFilter && (<>
      <div className="chatbot-side-title" role="tablist" aria-label="대화 보기">
        <button
          aria-selected={view === "active"}
          className={`chatbot-rooms-tab${view === "active" ? " is-active" : ""}`}
          onClick={() => onViewChange("active")}
          role="tab"
          type="button"
        >
          대화
        </button>
        <button
          aria-selected={view === "hidden"}
          className={`chatbot-rooms-tab${view === "hidden" ? " is-active" : ""}`}
          onClick={() => onViewChange("hidden")}
          role="tab"
          type="button"
        >
          숨긴 대화
        </button>
        <ChatTip title="새로고침">
          <button
            aria-label="목록 새로고침"
            className="chatbot-side-refresh"
            disabled={loading}
            onClick={onRefresh}
            type="button"
          >
            <RefreshIcon fontSize="inherit" />
          </button>
        </ChatTip>
      </div>

      <div className="chatbot-side-list" role="list">
        {error && (
          <p className="chatbot-side-empty is-error" role="alert">
            {error}{" "}
            <button className="chatbot-rooms-link" onClick={onRefresh} type="button">
              다시 시도
            </button>
          </p>
        )}
        {!error && !loading && rooms.length === 0 && (
          <p className="chatbot-side-empty">
            {view === "active"
              ? query
                ? "제목에 맞는 대화가 없습니다."
                : "아직 대화가 없습니다. 질문을 보내면 여기에 쌓입니다."
              : query
                ? "제목에 맞는 숨긴 대화가 없습니다."
                : "숨긴 대화가 없습니다."}
          </p>
        )}
        {rooms.map((room) => {
          const isSelected = room.idConv === selectedId;
          const isEditing = editing?.id === room.idConv;
          return (
            <div
              className={`chatbot-room${isSelected ? " is-selected" : ""}`}
              key={room.idConv}
              onContextMenu={isEditing ? undefined : (e) => openRoomMenu(e, room)}
              role="listitem"
            >
              {isEditing ? (
                <input
                  aria-label="대화 제목"
                  autoFocus
                  className="chatbot-room-rename"
                  maxLength={100}
                  onBlur={() => submitRename(room)}
                  onChange={(e) => setEditing({ id: room.idConv, title: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitRename(room);
                    } else if (e.key === "Escape") {
                      renameDoneRef.current = true;
                      setEditing(null);
                    }
                  }}
                  value={editing!.title}
                />
              ) : view === "hidden" ? (
                /* 숨긴 방은 누를 수 없다 — 열면 404 라 지금 대화만 지워진다. 되살린 뒤 연다 (코드리뷰 2026-10-08) */
                <div className="chatbot-side-chat is-static has-avatar">
                  <RoomAvatar label={menuGroup?.(room.cdMenu) ?? null} />
                  <span className="chatbot-side-chat-text">
                    <span className="chatbot-side-chat-top">
                      <span className="chatbot-side-chat-name">{roomTitle(room)}</span>
                      <span className="chatbot-side-chat-time">{formatRoomTime(room.tmHide)}</span>
                    </span>
                    <span className="chatbot-side-chat-meta">
                      {room.cdMenu ? `${menuName(room.cdMenu)} · 숨김` : "숨김"}
                    </span>
                  </span>
                </div>
              ) : (
                <button
                  aria-current={isSelected ? "true" : undefined}
                  className="chatbot-side-chat has-avatar"
                  onClick={() => onSelect(room)}
                  type="button"
                >
                  <RoomAvatar label={menuGroup?.(room.cdMenu) ?? null} />
                  <span className="chatbot-side-chat-text">
                    <span className="chatbot-side-chat-top">
                      <span className="chatbot-side-chat-name">{roomTitle(room)}</span>
                      <span className="chatbot-side-chat-time">{formatRoomTime(room.tmLast)}</span>
                    </span>
                    {/* 메뉴 없이 시작한 대화도 줄 높이를 맞춘다 */}
                    <span className="chatbot-side-chat-meta">{room.cdMenu ? menuName(room.cdMenu) : "\u00a0"}</span>
                  </span>
                </button>
              )}
            </div>
          );
        })}
        {loading && <p className="chatbot-side-empty">불러오는 중…</p>}
      </div>
      </>)}

      <Menu
        anchorPosition={roomMenu ? { top: roomMenu.y, left: roomMenu.x } : undefined}
        anchorReference="anchorPosition"
        onClose={() => setRoomMenu(null)}
        open={roomMenu !== null}
      >
        {view === "active"
          ? [
              <MenuItem
                dense
                key="rename"
                onClick={() => {
                  const room = roomMenu?.room;
                  setRoomMenu(null);
                  if (room) startRename(room);
                }}
              >
                <ListItemIcon>
                  <EditOutlinedIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>제목 바꾸기</ListItemText>
              </MenuItem>,
              <MenuItem
                dense
                key="hide"
                onClick={() => {
                  const room = roomMenu?.room;
                  setRoomMenu(null);
                  if (room) onHide(room);
                }}
              >
                <ListItemIcon>
                  <VisibilityOffOutlinedIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>숨기기</ListItemText>
              </MenuItem>,
            ]
          : [
              <MenuItem
                dense
                key="restore"
                onClick={() => {
                  const room = roomMenu?.room;
                  setRoomMenu(null);
                  if (room) onRestore(room);
                }}
              >
                <ListItemIcon>
                  <RestoreIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>되살리기</ListItemText>
              </MenuItem>,
            ]}
      </Menu>

      {view === "hidden" && (
        <p className="chatbot-side-empty chatbot-rooms-note">
          숨긴 대화는 목록과 AI 의 기억에서 빠집니다. 우클릭해 되살리면 다시 이어서 쓸 수 있습니다.
        </p>
      )}
    </aside>
  );
}
